import { ReactNode } from 'react'
import { useModal } from 'src/components/Modal/provider'
import { TelecomNumberType } from 'src/types/api'
import styled from 'styled-components'

export interface VulnerableCheckModalProps {
  title?: ReactNode
  children?: ReactNode
  confirmText?: ReactNode
  cancelText?: ReactNode
  onConfirm: () => void
  onCancel: () => void
  telecom: TelecomNumberType
}

const DEFAULT_TITLE = '서비스 안내'
const DEFAULT_CANCEL_TEXT = '취소'

const SKT_TITLE =
  '휴대폰쿠폰지갑은 만 75세 이상 고객님의 안전한 서비스 이용을 위해 아래 사항을 충분히 안내합니다.'

const SKT_CHILDREN = (
  <>
    본 서비스는 월 5,500원(VAT포함)의 유료서비스이며, 복잡한 디지털 조작이 필요할 수 있습니다.
    <br />
    언제든지 해지 가능합니다.
  </>
)

const SKT_CONFIRM = <>계속 진행하기</>
const SKT_CANCEL = <>중단하기</>

const KT_CHILDREN = (
  <>
    수많은 브랜드의 모바일 e-쿠폰을 상시 할인받고, 핫딜/무료쿠폰 등 다양한 혜택을 제공받을 수 있는
    <span style={{ color: '#FF4E4E' }}> 월 5,500원(VAT포함) 유료부가서비스</span>
    입니다.
    <br />
    가입을 진행하시겠습니까?
  </>
)

const KT_CONFIRM = (
  <>
    휴대폰쿠폰지갑
    <br />
    유료가입(월 5,500원)
  </>
)
const LGUP_TITLE = '안내사항'

const LgupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
`

const LgupSubTitle = styled.p`
  color: #ff4e4e;
  font-weight: 700;
`
const LgupContent = styled.p`
  color: #313338;
`

const LgupNotice = styled.p`
  color: #25282e;
  font-size: 15px;
  font-weight: 700;
`

const LGUP_CHILDREN = (
  <LgupContainer>
    <LgupSubTitle>유료 휴대폰쿠폰지갑 부가서비스 가입 확인</LgupSubTitle>
    <LgupContent>
      수 많은 브랜드의 모바일 e-쿠폰을 상시 할인받고, 핫딜/무료쿠폰 등 다양한 혜택을 드리며 [동의]를
      누르실 경우, 월 5,500원(VAT포함) 서비스 이용 요금이 부과되는 유료서비스 입니다.
    </LgupContent>
    <LgupNotice>동의 시 통신요금에 서비스 이용요금이 청구됩니다.</LgupNotice>
  </LgupContainer>
)

const LGUP_CONFIRM = <>동의</>

const VulnerableCheckModal = ({
  onConfirm,
  onCancel,
  title = DEFAULT_TITLE,
  children,
  confirmText,
  cancelText = DEFAULT_CANCEL_TEXT,
  telecom,
}: VulnerableCheckModalProps) => {
  const { closeModal } = useModal()

  let modalTitle = title
  let modalChildren = children
  let modalConfirmText = confirmText
  let modalCancelText = cancelText

  switch (telecom) {
    case 0:
      modalTitle = SKT_TITLE
      modalChildren = SKT_CHILDREN
      modalCancelText = SKT_CANCEL
      modalConfirmText = SKT_CONFIRM
      break
    case 1:
      modalChildren = KT_CHILDREN
      modalConfirmText = KT_CONFIRM
      break
    case 2:
      modalTitle = LGUP_TITLE
      modalChildren = LGUP_CHILDREN
      modalConfirmText = LGUP_CONFIRM
      break
    default:
      modalChildren = children
      modalConfirmText = confirmText
      modalCancelText = cancelText
  }

  const handleCancel = () => {
    onCancel()
    closeModal()
  }

  const handleConfirm = () => {
    onConfirm()
    closeModal()
  }

  return (
    <ModalPopup
      role="dialog"
      aria-modal="true"
      aria-labelledby="vulnerable-check-modal-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ModalTitle id="vulnerable-check-modal-title" $multiline={telecom === 0}>
        {modalTitle}
      </ModalTitle>
      <ModalContent>{modalChildren}</ModalContent>

      <ModalActions>
        <ModalBtn type="button" variant="cancel" onClick={handleCancel}>
          {modalCancelText}
        </ModalBtn>

        <ModalBtn type="button" variant="confirm" onClick={handleConfirm}>
          {modalConfirmText}
        </ModalBtn>
      </ModalActions>
    </ModalPopup>
  )
}

export default VulnerableCheckModal

const ModalPopup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 20px;
  background: #fff;
  font-family: 'Pretendard', sans-serif;

  && * {
    font-family: 'Pretendard', sans-serif;
  }
`

const ModalTitle = styled.div<{ $multiline?: boolean }>`
  color: #25282e;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  ${(p) => p.$multiline && `line-height: 1.4;`}
`

const ModalContent = styled.div`
  color: #313338;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  overflow-wrap: break-word;
  word-break: keep-all;
  line-height: 1.4;
`

const ModalActions = styled.div`
  display: flex;
  width: 100%;
  gap: 6px;
`

const ModalBtn = styled.button<{ variant: 'cancel' | 'confirm' }>`
  min-height: 48px;
  display: flex;
  padding: ${(p) => (p.variant === 'confirm' ? '4px 16px' : '4px 18px')};
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.6;
  cursor: pointer;

  ${(p) =>
    p.variant === 'cancel'
      ? `
    border: 1px solid #CCD0D8;
    background: #fff;
    color: #717580;
    font-weight: 500;
  `
      : `
    border: none;
    background: #000;
    color: #fff;
    font-weight: 700;
    letter-spacing: -0.04rem;
  `}
`
